/**
 * Upcoming & overdue payments card — shows a terracotta accent bar when any
 * payment is overdue. Self-hides when there's nothing to show.
 */
import React from 'react';
import { Card } from '../ui/card';
import { CreditCard, ArrowRight, WarningTriangle } from 'iconoir-react';
import { formatCurrency } from './constants';

function fmtShortDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function UpcomingPaymentsCard({ reminders, currency }) {
  const totalOverdue = reminders?.total_overdue || 0;
  const totalUpcoming = reminders?.total_upcoming || 0;
  if (totalOverdue === 0 && totalUpcoming === 0) return null;

  return (
    <Card
      className={`card-surface p-6 ${totalOverdue > 0 ? 'border-l-4 border-l-terracotta' : ''}`}
      data-testid="payment-reminders-card"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-muted" strokeWidth={1.5} />
          <h2 className="font-serif text-xl text-charcoal">Upcoming Payments</h2>
          {totalOverdue > 0 && (
            <span className="flex items-center gap-1 text-xs text-terracotta font-medium">
              <WarningTriangle className="w-3.5 h-3.5" /> {totalOverdue} overdue
            </span>
          )}
        </div>
        <a href="/payments" className="text-terracotta hover:text-terracotta-dark text-xs flex items-center gap-1">
          View all <ArrowRight className="w-3 h-3" />
        </a>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {reminders.overdue?.map((p) => (
          <div key={p.id} className="flex items-center justify-between p-3 bg-terracotta/5 rounded-sm border border-terracotta/20">
            <div>
              <span className="text-sm text-charcoal">{p.description}</span>
              <p className="text-xs text-terracotta">Overdue</p>
            </div>
            <span className="font-mono text-sm text-terracotta">{formatCurrency(p.amount, currency)}</span>
          </div>
        ))}
        {reminders.upcoming?.slice(0, 4).map((p) => (
          <div key={p.id} className="flex items-center justify-between p-3 bg-page rounded-sm border border-border">
            <div>
              <span className="text-sm text-charcoal">{p.description}</span>
              <p className="text-xs text-muted">Due {fmtShortDate(p.due_date)}</p>
            </div>
            <span className="font-mono text-sm text-charcoal">{formatCurrency(p.amount, currency)}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}
